/*
  gg-reviews.js — GreenGlam-native reviews data layer (rvx0 / rv-B core).

  A <gg-reviews> custom element wrapping the server-rendered block markup
  (blocks/gg-reviews.liquid). It speaks the gg_flow-api contract directly —
  it is NOT a fork of the vendor snippet's skin. The visible headline + first
  preview review are already in the initial HTML (Liquid, zero-JS, SEO); this
  module enhances with the live list, rotation, sort, pagination and the write
  form (rv-C/D/E/G build on this core).

  Contract (honor exactly):
    GET  {API_BASE}?product_id&sort&page&per_page
         → { aggregate:{average,total}, reviews:[…], titles:[…] }
    POST {API_BASE}
         { product_id, title, body, rating, author_name, email, website, token }
         → { success: true }
    review fields: id, title, body, rating, author_name, created_at,
                   verified_purchase, helpful_count, reply_body, reply_at

  Structural fixes vs. the vendor inline JS:
   • class + connectedCallback → methods exist before init (kills the
     "_bindIds is not a function" init-order bug by construction).
   • AbortController per fetch; previous request aborted on sort/filter change.
   • Number(...) coercion — the API may return numeric strings; a bare
     .toFixed() would throw and abort the whole render.
   • ?rvt token read ONCE at construction (survives the variant-picker URL
     rewrite that happens after load).
   • Each fetch .catch() degrades only its own feature — the server-rendered
     headline always survives.
*/

(function () {
  'use strict';

  const DEFAULTS = {
    apiBase: 'https://api.greenglam.biz/api/reviews',
    perPage: 10,
    minReviews: 1,
    tickerInterval: 3200,
    sort: 'recent',
  };

  class GgReviews extends HTMLElement {
    connectedCallback() {
      if (this.#inited) return;
      this.#inited = true;

      // ── Config (data-*, all with defaults — product.json is editor-clobberable) ──
      const d = this.dataset;
      this.productId = d.productId || '';
      this.apiBase = d.apiBase || DEFAULTS.apiBase;
      this.perPage = parseInt(d.perPage || DEFAULTS.perPage, 10);
      this.minReviews = parseInt(d.minReviews || DEFAULTS.minReviews, 10);
      this.currentSort = d.defaultSort || DEFAULTS.sort;
      this.rotateEnabled = d.rotatePreview !== 'false';
      this.rotateInterval = parseInt(d.rotateInterval || DEFAULTS.tickerInterval, 10);
      this.showDistribution = d.showDistribution !== 'false';
      this.enableHelpful = d.enableHelpful === 'true' && !!d.helpfulEndpoint;
      this.helpfulEndpoint = d.helpfulEndpoint || '';
      this.showReplies = d.showReplies !== 'false';
      this.showVerified = d.showVerified !== 'false';

      // ── State ──
      this.data = null;
      this.reviews = [];          // rotation pool (recent from #load)
      this.listReviews = [];      // accumulated list (may be re-sorted/paged)
      this.currentPage = 1;
      this.totalLoaded = 0;
      this.reachedEnd = false;
      this.listLoaded = false;
      this.selectedStars = 0;
      this.activeRatingFilter = null;
      this.listOpen = false;
      this.formOpen = false;
      this.#rotateIdx = 0;
      // Token read ONCE — later URL rewrites (variant picker) won't strip it.
      this.reviewToken = this.#readToken();

      this.#bind();
      this.#wireEvents();

      if (!this.productId) return; // nothing to do without a product
      this.#load();
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#stopRotation();
      this.#io?.disconnect();
    }

    // ── private fields ──
    #inited = false;
    #abort = null;
    #rotateTimer = null;
    #rotateIdx = 0;
    #io = null;
    #onScreen = true;
    #els = {};

    #bind() {
      const $ = (s) => this.querySelector(s);
      this.#els = {
        score: $('.gg-reviews__score'),
        previewCard: $('.gg-reviews__preview-card'),
        write: $('.gg-reviews__write'),
        showAll: $('.gg-reviews__show-all'),
        expand: $('.gg-reviews__expand'),
        listRegion: $('.gg-reviews__list-region'),
        list: $('.gg-reviews__list'),
        sort: $('.gg-reviews__sort'),
        more: $('.gg-reviews__more'),
        form: $('.gg-reviews__form'),
        formStars: $('.gg-reviews__form-stars'),
        collapse: $('.gg-reviews__collapse'),
        success: $('.gg-reviews__form-msg--success'),
        error: $('.gg-reviews__form-msg--error'),
      };
      if (this.#els.sort) this.#els.sort.value = this.currentSort;
    }

    #wireEvents() {
      const e = this.#els;
      e.showAll?.addEventListener('click', () => this.#toggleList());
      e.write?.addEventListener('click', () => this.#toggleForm());
      e.collapse?.addEventListener('click', () => this.#collapseAll(true));
      e.sort?.addEventListener('change', () => {
        this.currentSort = e.sort.value;
        this.#loadList();
      });
      e.more?.addEventListener('click', () => this.#loadMore());
      e.form?.addEventListener('submit', (ev) => this.#onSubmit(ev));
      // Delegated helpful-vote clicks (cards are rendered as innerHTML).
      e.list?.addEventListener('click', (ev) => {
        const btn = ev.target.closest('.gg-reviews__helpful');
        if (btn) this.#onHelpful(btn);
      });
      this.#wireStarPicker();

      // Rotation pauses on hover/focus over the preview column.
      e.previewCard?.addEventListener('mouseenter', () => this.#pauseRotation());
      e.previewCard?.addEventListener('mouseleave', () => this.#resumeRotation());
      e.previewCard?.addEventListener('focusin', () => this.#pauseRotation());
      e.previewCard?.addEventListener('focusout', () => this.#resumeRotation());

      // Escape collapses the inline region (NOT a modal — no focus trap).
      this.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && (this.listOpen || this.formOpen)) {
          this.#collapseAll(true);
        }
      });
    }

    #readToken() {
      try {
        return new URLSearchParams(window.location.search).get('rvt') || '';
      } catch (e) {
        return '';
      }
    }

    // ─────────────────────────────────────────────────────────────
    //  API
    // ─────────────────────────────────────────────────────────────
    async #load() {
      try {
        const data = await this.#get({ page: 1, sort: this.currentSort });
        this.data = data;
        this.reviews = Array.isArray(data.reviews) ? data.reviews : [];
        this.#onData(data);
      } catch (e) {
        // Server-rendered headline already in the DOM — stay silent.
      }
    }

    // GET with AbortController; aborts any in-flight request first.
    async #get({ page = 1, sort = this.currentSort } = {}) {
      this.#abort?.abort();
      this.#abort = new AbortController();
      let url =
        this.apiBase +
        '?product_id=' + encodeURIComponent(this.productId) +
        '&sort=' + encodeURIComponent(sort) +
        '&page=' + page +
        '&per_page=' + this.perPage;
      if (this.activeRatingFilter) url += '&rating=' + this.activeRatingFilter;
      const res = await fetch(url, { signal: this.#abort.signal });
      return res.json();
    }

    #onData(data) {
      const total = this.#total(data);
      // Refresh the visible score from authoritative live data when present.
      if (total >= this.minReviews && data.aggregate && this.#els.score) {
        this.#els.score.textContent = this.#avg(data).toFixed(1);
      }
      this.#renderDistribution(data);
      this.#startRotation();
      this.dispatchEvent(new CustomEvent('gg-reviews:data', { detail: data }));
    }

    // ─────────────────────────────────────────────────────────────
    //  Inline-expand state machine (rv-C)
    // ─────────────────────────────────────────────────────────────
    #toggleList() {
      this.#setListOpen(!this.listOpen);
    }

    #setListOpen(open) {
      if (open === this.listOpen) return;
      this.listOpen = open;
      this.#els.listRegion.hidden = !open;
      const sa = this.#els.showAll;
      if (sa) {
        sa.setAttribute('aria-expanded', String(open));
        // Toggle the trigger label so it reads "Weniger anzeigen" while open.
        const next = open ? sa.dataset.labelHide : sa.dataset.labelShow;
        if (next) sa.textContent = next;
      }
      if (open && !this.listLoaded) this.#loadList();
      this.#syncExpanded();
      if (open) this.#els.sort?.focus();
    }

    #toggleForm() {
      this.#setFormOpen(!this.formOpen);
    }

    #setFormOpen(open) {
      if (open === this.formOpen) return;
      this.formOpen = open;
      this.#els.form.hidden = !open;
      this.#els.write?.setAttribute('aria-expanded', String(open));
      this.#syncExpanded();
      if (open) this.querySelector('.gg-reviews__star-pick')?.focus();
    }

    #syncExpanded() {
      const anyOpen = this.listOpen || this.formOpen;
      this.#els.expand.hidden = !anyOpen;
      this.classList.toggle('is-expanded', anyOpen);
      if (anyOpen) this.#pauseRotation();
      else this.#resumeRotation();
    }

    #collapseAll(returnFocus) {
      const trigger = this.formOpen ? this.#els.write : this.#els.showAll;
      this.#setListOpen(false);
      this.#setFormOpen(false);
      if (returnFocus) trigger?.focus();
    }

    // ─────────────────────────────────────────────────────────────
    //  Preview rotation (rv-C)
    // ─────────────────────────────────────────────────────────────
    #startRotation() {
      if (this.#rotateTimer || !this.rotateEnabled) return;
      if (this.reviews.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // Pause when off-screen.
      if (!this.#io && 'IntersectionObserver' in window) {
        this.#io = new IntersectionObserver((entries) => {
          this.#onScreen = entries[0].isIntersecting;
          if (this.#onScreen) this.#resumeRotation();
          else this.#pauseRotation();
        });
        this.#io.observe(this);
      }
      this.#rotateTimer = setInterval(() => this.#tick(), this.rotateInterval);
    }

    #tick() {
      if (this.listOpen || this.formOpen) return;
      this.#rotateIdx = (this.#rotateIdx + 1) % this.reviews.length;
      const next = this.reviews[this.#rotateIdx];
      const card = this.#els.previewCard;
      if (!card) return this.#renderPreview(next);
      // Fade the card out, swap content while invisible, fade back in.
      card.classList.add('is-fading');
      window.setTimeout(() => {
        this.#renderPreview(next);
        card.classList.remove('is-fading');
      }, 300); // matches the CSS opacity transition
    }

    #renderPreview(r) {
      const card = this.#els.previewCard;
      if (!card || !r) return;
      card.innerHTML =
        '<div class="gg-reviews__item-head">' +
          '<p class="gg-reviews__item-title">' + this.#esc(r.title) + '</p>' +
          '<div class="gg-reviews__stars" aria-label="' + (Number(r.rating) || 0) + ' / 5">' +
            this.#starsHTML(r.rating) +
          '</div>' +
        '</div>' +
        '<p class="gg-reviews__body">' + this.#esc(r.body) + '</p>';
    }

    #pauseRotation() {
      if (this.#rotateTimer) {
        clearInterval(this.#rotateTimer);
        this.#rotateTimer = null;
      }
    }

    #resumeRotation() {
      if (this.listOpen || this.formOpen) return;
      if (this.#onScreen === false) return;
      this.#startRotation();
    }

    #stopRotation() {
      this.#pauseRotation();
    }

    // ─────────────────────────────────────────────────────────────
    //  Review list + sort + append-pagination (rv-D)
    // ─────────────────────────────────────────────────────────────
    async #loadList() {
      // sort/filter change → reset to page 1 and REPLACE the list.
      this.currentPage = 1;
      this.reachedEnd = false;
      this.#els.list.innerHTML = '<p class="gg-reviews__body gg-reviews__body--empty">…</p>';
      try {
        const data = await this.#get({ page: 1, sort: this.currentSort });
        const reviews = Array.isArray(data.reviews) ? data.reviews : [];
        this.listReviews = reviews;
        this.totalLoaded = reviews.length;
        this.listLoaded = true;
        this.#els.list.innerHTML = reviews.length
          ? reviews.map((r) => this.cardHTML(r)).join('')
          : '<p class="gg-reviews__body gg-reviews__body--empty">—</p>';
        this.#updateEnd(reviews.length, data);
      } catch (e) {
        if (e.name === 'AbortError') return;
        this.#els.list.innerHTML =
          '<p class="gg-reviews__body gg-reviews__body--empty">' +
          this.#esc(this.dataset.msgLoadError || 'Reviews could not be loaded.') +
          '</p>';
      }
    }

    async #loadMore() {
      if (this.reachedEnd) return;
      this.currentPage += 1;
      try {
        const data = await this.#get({ page: this.currentPage, sort: this.currentSort });
        const reviews = Array.isArray(data.reviews) ? data.reviews : [];
        this.listReviews = this.listReviews.concat(reviews);
        this.totalLoaded += reviews.length;
        // Append — never innerHTML= (that would wipe earlier pages).
        this.#els.list.insertAdjacentHTML('beforeend', reviews.map((r) => this.cardHTML(r)).join(''));
        this.#updateEnd(reviews.length, data);
      } catch (e) {
        if (e.name !== 'AbortError') this.currentPage -= 1; // allow retry
      }
    }

    // No total-pages from the API — infer the end from page size / accumulated total.
    #updateEnd(lastCount, data) {
      const total = this.#total(data) || this.#total(this.data);
      this.reachedEnd = lastCount < this.perPage || (total && this.totalLoaded >= total);
      if (this.#els.more) this.#els.more.hidden = this.reachedEnd || this.totalLoaded === 0;
    }

    // ─────────────────────────────────────────────────────────────
    //  Rating distribution + tap-to-filter (rv-G; degrades without data)
    // ─────────────────────────────────────────────────────────────
    #renderDistribution(data) {
      if (!this.showDistribution) return;
      // Backend addendum (rv-K) must supply per-star counts; degrade silently.
      const dist = data && (data.distribution || (data.aggregate && data.aggregate.distribution));
      if (!dist) return;
      let region = this.querySelector('.gg-reviews__distribution');
      if (!region) {
        region = document.createElement('div');
        region.className = 'gg-reviews__distribution';
        this.#els.listRegion?.prepend(region);
      }
      const total = this.#total(data) || 1;
      let html = '';
      for (let star = 5; star >= 1; star--) {
        const count = Number(dist[star] || dist['' + star] || 0);
        const pct = Math.round((count / total) * 100);
        html +=
          '<button type="button" class="gg-reviews__dist-row' +
          (this.activeRatingFilter === star ? ' is-active' : '') +
          '" data-rating="' + star + '">' +
          '<span>' + star + '★</span>' +
          '<span class="gg-reviews__dist-track"><span class="gg-reviews__dist-fill" style="width:' + pct + '%"></span></span>' +
          '<span class="gg-reviews__dist-count">' + count + '</span>' +
          '</button>';
      }
      region.innerHTML = html;
      region.querySelectorAll('.gg-reviews__dist-row').forEach((row) => {
        row.addEventListener('click', () => {
          const star = Number(row.dataset.rating);
          this.activeRatingFilter = this.activeRatingFilter === star ? null : star;
          this.#setListOpen(true);
          this.#loadList();
          this.#renderDistribution(this.data);
        });
      });
    }

    // ─────────────────────────────────────────────────────────────
    //  Card rendering (OUR .gg-reviews__* markup — no --rv-* skin)
    // ─────────────────────────────────────────────────────────────
    cardHTML(r) {
      const rating = Number(r.rating) || 0;
      const verified =
        this.showVerified && r.verified_purchase
          ? '<span class="gg-reviews__verified">✓ ' + this.#esc(this.dataset.verifiedLabel || 'Verified purchase') + '</span>'
          : '';
      const helpful = this.enableHelpful
        ? '<button type="button" class="gg-reviews__helpful" data-id="' + this.#esc(r.id) + '">' +
          this.#esc(this.dataset.helpfulLabel || 'Helpful') +
          ' <span class="gg-reviews__helpful-count">' + (Number(r.helpful_count) || 0) + '</span>' +
          '</button>'
        : '';
      const reply =
        this.showReplies && r.reply_body && String(r.reply_body).trim()
          ? '<div class="gg-reviews__reply">' +
            '<span class="gg-reviews__reply-label">' + this.#esc(this.dataset.replyLabel || 'Reply from GreenGlam') + '</span>' +
            '<p class="gg-reviews__reply-body">' + this.#esc(r.reply_body) + '</p>' +
            '</div>'
          : '';
      return (
        '<article class="gg-reviews__card">' +
          '<div class="gg-reviews__item-head">' +
            '<p class="gg-reviews__item-title">' + this.#esc(r.title) + '</p>' +
            '<span class="gg-reviews__stars" aria-label="' + rating + ' / 5">' + this.#starsHTML(rating) + '</span>' +
          '</div>' +
          '<p class="gg-reviews__body">' + this.#esc(r.body) + '</p>' +
          '<div class="gg-reviews__card-meta">' +
            '<span class="gg-reviews__author">' + this.#esc(r.author_name) + '</span>' +
            '<span class="gg-reviews__date">' + this.#formatDate(r.created_at) + '</span>' +
            verified +
            helpful +
          '</div>' +
          reply +
        '</article>'
      );
    }

    // ─────────────────────────────────────────────────────────────
    //  Star picker (rv-E)
    // ─────────────────────────────────────────────────────────────
    #wireStarPicker() {
      const stars = this.querySelectorAll('.gg-reviews__star-pick');
      stars.forEach((star) => {
        const val = parseInt(star.dataset.value, 10);
        star.addEventListener('click', () => this.#selectStar(val));
        star.addEventListener('mouseenter', () => this.#highlightStars(val));
        star.addEventListener('mouseleave', () => this.#highlightStars(this.selectedStars));
      });
    }

    #selectStar(value) {
      this.selectedStars = value;
      this.#highlightStars(value);
      this.querySelectorAll('.gg-reviews__star-pick').forEach((s, i) => {
        s.setAttribute('aria-checked', i < value ? 'true' : 'false');
      });
    }

    #highlightStars(value) {
      this.querySelectorAll('.gg-reviews__star-pick').forEach((s, i) => {
        s.classList.toggle('is-active', i < value);
      });
    }

    // ─────────────────────────────────────────────────────────────
    //  Submit handler (rv-E) — validate, POST, success/error UI
    // ─────────────────────────────────────────────────────────────
    async #onSubmit(ev) {
      ev.preventDefault();
      const f = this.#els.form;
      const ds = this.dataset;
      const val = (name) => (f.elements[name]?.value || '').trim();
      const title = val('title');
      const body = val('body');
      const name = val('author_name');
      const email = val('email');
      const website = val('website'); // honeypot

      this.#els.error.hidden = true;
      if (!this.selectedStars) return this.#showError(ds.errRating);
      if (!title) return this.#showError(ds.errTitle);
      if (!name) return this.#showError(ds.errName);
      if (!email || email.indexOf('@') === -1) return this.#showError(ds.errEmail);

      const submit = f.querySelector('.gg-reviews__submit');
      const label = submit.textContent;
      submit.disabled = true;
      submit.textContent = ds.msgSending || '…';

      try {
        const data = await this.submitReview({
          title, body, rating: this.selectedStars, authorName: name, email, website,
        });
        if (data && data.success) {
          this.#els.success.textContent = ds.msgSuccess || '';
          this.#els.success.hidden = false;
          submit.style.display = 'none';
          // Reload the list (page 1) so the review shows if auto-approved.
          if (this.listLoaded) this.#loadList();
        } else {
          this.#showError((data && data.error) || ds.errGeneric);
          submit.disabled = false;
          submit.textContent = label;
        }
      } catch (e) {
        this.#showError(ds.errGeneric);
        submit.disabled = false;
        submit.textContent = label;
      }
    }

    #showError(msg) {
      this.#els.error.textContent = msg || '';
      this.#els.error.hidden = false;
    }

    // ─────────────────────────────────────────────────────────────
    //  Helpful voting (rv-G; needs backend endpoint — degrades off)
    // ─────────────────────────────────────────────────────────────
    async #onHelpful(btn) {
      if (!this.helpfulEndpoint || btn.disabled) return;
      btn.disabled = true;
      try {
        await fetch(this.helpfulEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_id: this.productId, review_id: btn.dataset.id }),
        });
        const countEl = btn.querySelector('.gg-reviews__helpful-count');
        if (countEl) countEl.textContent = (Number(countEl.textContent) || 0) + 1;
      } catch (e) {
        btn.disabled = false; // allow retry on failure
      }
    }

    // ─────────────────────────────────────────────────────────────
    //  Submit (POST contract + honeypot + token)
    // ─────────────────────────────────────────────────────────────
    async submitReview({ title, body, rating, authorName, email, website }) {
      const res = await fetch(this.apiBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: this.productId,
          title: title,
          body: body,
          rating: rating,
          author_name: authorName,
          email: email,
          website: website || '', // honeypot — server-evaluated
          token: this.reviewToken || '',
        }),
      });
      return res.json();
    }

    // ─────────────────────────────────────────────────────────────
    //  Helpers
    // ─────────────────────────────────────────────────────────────
    #total(data) {
      return data && data.aggregate ? Number(data.aggregate.total) || 0 : 0;
    }

    #avg(data) {
      return data && data.aggregate ? Number(data.aggregate.average) || 0 : 0;
    }

    #esc(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    // Glyph stars (full ★ / empty ☆) — matches the block's .gg-star treatment.
    #starsHTML(rating) {
      const full = Math.round(Number(rating) || 0);
      let out = '';
      for (let i = 1; i <= 5; i++) {
        out += i <= full
          ? '<span class="gg-star gg-star--full">★</span>'
          : '<span class="gg-star gg-star--empty">★</span>';
      }
      return out;
    }

    #formatDate(dateStr) {
      try {
        return new Date(dateStr).toLocaleDateString(document.documentElement.lang || 'de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      } catch (e) {
        return String(dateStr || '');
      }
    }
  }

  if (!customElements.get('gg-reviews')) {
    customElements.define('gg-reviews', GgReviews);
  }
})();
